import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { ClientProfilePanel } from '../components/ClientProfilePanel';
import { ScenarioCard } from '../components/ScenarioCard';
import { ActionsBar } from '../components/ActionsBar';
import { Button } from '../components/ui/Button';
import { LoanScenario } from '../utils/mortgageCalculations';
// Mock Client Data (would come from Salesforce context)
const MOCK_CLIENT = {
  name: 'John & Sarah Smith',
  ficoScore: 742,
  monthlyIncome: 12500,
  monthlyDebts: 1850,
  existingLoans: 0
};
const DEFAULT_SCENARIO: LoanScenario = {
  id: '1',
  name: 'Scenario A',
  loanAmount: 450000,
  interestRate: 6.5,
  termYears: 30,
  downPayment: 50000,
  propertyValue: 500000,
  productType: 'Fixed'
};
export function MortgageCalculator() {
  const [scenarios, setScenarios] = useState<LoanScenario[]>([DEFAULT_SCENARIO]);
  const handleScenarioChange = (id: string, field: keyof LoanScenario, value: any) => {
    setScenarios(prev => prev.map(s => s.id === id ? {
      ...s,
      [field]: value
    } : s));
  };
  const addScenario = () => {
    if (scenarios.length >= 3) return;
    const newId = Math.random().toString(36).substr(2, 9);
    const baseScenario = scenarios[scenarios.length - 1] || DEFAULT_SCENARIO;
    setScenarios([...scenarios, {
      ...baseScenario,
      id: newId,
      name: `Scenario ${String.fromCharCode(65 + scenarios.length)}`
    }]);
  };
  const duplicateScenario = (scenario: LoanScenario) => {
    if (scenarios.length >= 3) return;
    const newId = Math.random().toString(36).substr(2, 9);
    setScenarios([...scenarios, {
      ...scenario,
      id: newId,
      name: `${scenario.name} (Copy)`
    }]);
  };
  const removeScenario = (id: string) => {
    setScenarios(prev => prev.filter(s => s.id !== id));
  };
  const resetAll = () => {
    setScenarios([DEFAULT_SCENARIO]);
  };
  return <div className="min-h-screen bg-slate-100 pb-24" data-id="element-117">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20" data-id="element-118">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-119">
          <div className="flex items-center gap-3" data-id="element-120">
            <div className="bg-blue-600 p-2 rounded-lg" data-id="element-121">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-122">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 3.666V3m0 7.666V19.5a1.5 1.5 0 01-1.5 1.5H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" data-id="element-123" />
              </svg>
            </div>
            <div data-id="element-124">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-125">
                Mortgage Scenario Calculator
              </h1>
              <p className="text-xs text-slate-500" data-id="element-126">
                MPros Advisor Console v2.4
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4" data-id="element-127">
            <span className="text-sm text-slate-500" data-id="element-128">
              Last synced: Just now
            </span>
            <div className="h-8 w-8 rounded-full bg-slate-200 border-2 border-white shadow-sm overflow-hidden" data-id="element-129">
              <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix" alt="Advisor" data-id="element-130" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-131">
        <ClientProfilePanel client={MOCK_CLIENT} data-id="element-132" />

        <div className="mb-6 flex items-center justify-between" data-id="element-133">
          <h2 className="text-lg font-semibold text-slate-800" data-id="element-134">
            Active Scenarios
          </h2>
          {scenarios.length < 3 && <Button onClick={addScenario} variant="outline" className="border-dashed border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600" data-id="element-135">
              <Plus className="mr-2 h-4 w-4" data-id="element-136" />
              Add Scenario
            </Button>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-137">
          {scenarios.map(scenario => <ScenarioCard key={scenario.id} scenario={scenario} clientIncome={MOCK_CLIENT.monthlyIncome} clientDebts={MOCK_CLIENT.monthlyDebts} onChange={handleScenarioChange} onRemove={removeScenario} onDuplicate={duplicateScenario} isRemovable={scenarios.length > 1} data-id="element-138" />)}

          {/* Empty State Placeholder if fewer than 3 scenarios */}
          {scenarios.length < 3 && <div onClick={addScenario} className="border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center p-12 text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 transition-all cursor-pointer h-full min-h-[500px]" data-id="element-139">
              <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mb-4 group-hover:bg-blue-100" data-id="element-140">
                <Plus className="h-8 w-8" data-id="element-141" />
              </div>
              <h3 className="font-semibold text-lg" data-id="element-142">Add Comparison Scenario</h3>
              <p className="text-sm mt-2 text-center max-w-[200px]" data-id="element-143">
                Create another scenario to compare terms side-by-side
              </p>
            </div>}
        </div>
      </main>

      <ActionsBar onReset={resetAll} onSave={() => alert('Scenarios saved to Salesforce Opportunity #84920')} onGeneratePDF={() => alert('Generating PDF Summary...')} onEmail={() => alert('Email draft created for client')} data-id="element-144" />
    </div>;
}